import { useEffect, useRef, useState } from 'react';

/** Shared with PartnerSpinner: under this, showing anything is worse than not. */
export const LOADER_APPEAR_AFTER_MS = 200;

/**
 * Splits one `loading` flag into "first load" and "reload".
 *
 * Every partner screen holds a single boolean and folds partner id, search
 * text, filters and page number into one useCallback, so the same flag fires
 * for mount, for a debounced keystroke and for a page change. That is fine for
 * fetching but not for presentation: a first load has nothing on screen and
 * wants a spinner, whereas a search already has a table on screen and wants
 * skeleton rows in place of the data — blanking the whole table behind an
 * overlay is what loses the user their context.
 *
 * `settled` latches on the first completed request, successful or not. An
 * error still counts: the screen has rendered its answer, so the next fetch is
 * a reload by any definition the user would recognise.
 *
 * Deliberately not derived from `rows.length`. That heuristic reads a search
 * with no matches as "no data yet", so the search after an empty one would
 * flip back to the spinner — the one moment the table is most in need of
 * staying put. It also misreads an error (every loader empties its rows on
 * failure) and a partner switch the same way.
 *
 * The latch waits for a load to have STARTED before it can settle, because
 * BookingListPage initialises its flag to `false` rather than `true` like its
 * siblings. Latching on "not loading" alone would settle on that first idle
 * render and label the screen's genuine first fetch a reload.
 *
 * `refetching` also waits out LOADER_APPEAR_AFTER_MS. The ring already hides
 * itself for that long; without the same grace here a stubbed search
 * (specClient resolves fixtures in 120ms) would flash rows to shimmer and back
 * inside two frames, which is worse than showing nothing at all. Sharing one
 * constant is what stops the two from drifting apart.
 *
 * @param {boolean} loading the screen's existing loading flag
 * @returns {{initialLoading: boolean, refetching: boolean}}
 */
export default function useLoadPhase(loading) {
  const [settled, setSettled] = useState(false);
  const [slow, setSlow] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (loading) {
      started.current = true;
      const timer = setTimeout(() => setSlow(true), LOADER_APPEAR_AFTER_MS);
      return () => clearTimeout(timer);
    }
    setSlow(false);
    if (started.current) setSettled(true);
    return undefined;
  }, [loading]);

  return {
    initialLoading: loading && !settled,
    refetching: loading && settled && slow,
  };
}
